import { CheckoutShell } from '@/components/CheckoutShell';
import { isLocalhostAlias, LOCAL_DEV_DOCS_URL, TEST_MODE_DOCS_URL } from '@/localDev';

interface Props {
  // The local hostname the page is running on (localhost, 127.0.0.1, …),
  // shown so the developer can see what the widget resolved.
  host: string;
  // The page's port, so the suggested URL matches the dev server they
  // already have running. Empty for standard ports.
  port: string;
  // notSetup narrows the failure to "the .localhost alias resolved, but that
  // domain has never begun SM setup" (the backend's 404 "Domain not set up
  // for Subscriptions") — the fix is finishing setup, not the alias advice.
  notSetup?: boolean;
  onDismiss?: () => void;
}

// LocalDevStep replaces the generic load-failure error when a tenant-scoped
// config fetch fails on a local origin: the page hostname can never resolve a
// site, so the only audience is the developer integrating SM. Copy is
// hardcoded English on purpose — the i18n catalog's key-drift guard requires
// every key in all 40+ shipped locales, and a localhost-only developer surface
// should not ship translation slots.
export function LocalDevStep({ host, port, notSetup, onDismiss }: Props) {
  const portSuffix = port && port !== '80' && port !== '443' ? `:${port}` : '';
  // The alias resolved but the domain has no SM setup: name the registered
  // domain (strip the alias suffix when present) and the real fix — finishing
  // setup. In practice only a .localhost alias reaches this state; a bare
  // local host never resolves.
  const baseDomain = host.replace(/\.localhost$/i, '');
  const guidance = () => {
    if (notSetup) {
      return (
        <p class="sm-subtitle" role="alert">
          <code>{host}</code> resolves to <code>{baseDomain}</code>, which isn't set up for Ezoic
          Subscriptions — checkout is unavailable until setup is finished in your Ezoic dashboard.
          See <a href={LOCAL_DEV_DOCS_URL} target="_blank" rel="noopener noreferrer">local development guidance</a>.
        </p>
      );
    }
    if (isLocalhostAlias(host)) {
      return (
        // Already on a *.localhost alias that resolved nothing: the fix is the
        // full-domain form, not the alias advice they already followed.
        <p class="sm-subtitle" role="alert">
          No site in your Ezoic account matches <code>{host}</code>. The domain before <code>.localhost</code> must
          be the full registered domain — <code>mysite.com.localhost</code>, not <code>mysite.localhost</code>. See{' '}
          <a href={LOCAL_DEV_DOCS_URL} target="_blank" rel="noopener noreferrer">local development guidance</a>.
        </p>
      );
    }
    return (
      // Bare local host: the widget resolves the tenant from the page
      // hostname, so the developer needs a hostname that matches their
      // registered site. The .localhost alias is the smaller change; the
      // hosts file is the fallback when that address does not resolve.
      <div role="alert">
        <p class="sm-subtitle">
          This address doesn't match a site in your Ezoic account.
        </p>
        <div class="sm-localdev-options">
          <p class="sm-localdev-lead">To develop locally, do one of the following:</p>
          <div>
            <span class="sm-localdev-easiest">Easiest</span>
            <p>
              Open <code>http://&lt;yourdomain&gt;.localhost{portSuffix}</code>
            </p>
            <p class="sm-localdev-example">
              Example: <code>http://mysite.com.localhost{portSuffix}</code>
            </p>
          </div>
          <div class="sm-or"><span>or</span></div>
          <p>
            Add <code>127.0.0.1 &lt;yourdomain&gt;</code> to your hosts file and open the site from that hostname.
          </p>
          <p class="sm-localdev-example">
            Example: <code>http://mysite.com{portSuffix}</code>
          </p>
        </div>
        <p class="sm-subtitle">
          See <a href={LOCAL_DEV_DOCS_URL} target="_blank" rel="noopener noreferrer">local development guidance</a>
        </p>
      </div>
    );
  };
  return (
    <CheckoutShell ariaLabel="Local development" currentStep={1} hideSteps onDismiss={onDismiss}>
      <div class="sm-localdev-head">
        <div class="sm-info-mark" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9" />
            <line x1="12" y1="11" x2="12" y2="16" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </div>
        <h2 class="sm-title">Local development detected</h2>
      </div>
      <p class="sm-subtitle">
        This page is served from <code>{host}{portSuffix}</code>.
      </p>
      {guidance()}

      <div class="sm-info-panel">
        <strong>Live checkout requires HTTPS</strong>
        <p>
          <a href={TEST_MODE_DOCS_URL} target="_blank" rel="noopener noreferrer">Test mode</a> works over HTTP —
          use test mode to verify your integration with test payment methods.
        </p>
      </div>
    </CheckoutShell>
  );
}
